'use client'

import { useState } from 'react'
import Link from 'next/link'
import { ArrowUpRight, Loader2 } from 'lucide-react'
import api from '@/lib/api'
import { VelovaLogo } from '@/components/velova-logo'
import { SITE_META } from '@/lib/site-config'

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // TODO(auth): backend/src/routes/auth.routes.ts has no
      // POST /auth/forgot-password route yet — wire it up when it exists.
      // Until then this call 404s; the outcome is intentionally ignored
      // below so the UI never leaks whether an account exists.
      await api.post('/auth/forgot-password', { email })
    } catch {
      // Generic-success semantics: show the same confirmation regardless
      // of whether the account exists or the endpoint is live.
    } finally {
      setLoading(false)
      setSubmitted(true)
    }
  }

  return (
    <main className="velova-canvas velova-grain min-h-screen flex flex-col">
      <div className="p-5 sm:p-8 flex items-center justify-between border-b border-[var(--velova-line-strong)]">
        <Link href="/" className="flex items-center gap-3">
          <VelovaLogo variant="light" width={100} />
        </Link>
        <span className="velova-mono-label opacity-60">§ Reset</span>
      </div>

      <div className="flex-1 flex items-center justify-center p-5 sm:p-8">
        <div className="w-full max-w-md">
          {submitted ? (
            <div>
              <div className="velova-mono-label opacity-60 mb-4">Reset password</div>
              <h1 className="velova-display text-[32px] sm:text-[40px] leading-[0.95]">
                Check your{' '}
                <span className="velova-italic" style={{ color: 'var(--velova-blue-deep)' }}>
                  inbox
                </span>
                .
              </h1>
              <p className="text-[14px] opacity-70 mt-4 leading-relaxed">
                If the address is registered, a reset link is on its way.
              </p>

              <div className="velova-hr my-8" />

              <Link
                href="/login"
                className="flex items-center gap-1.5 text-[13px] hover:opacity-70 transition-opacity"
              >
                Return to sign in
                <ArrowUpRight className="w-[12px] h-[12px]" />
              </Link>
            </div>
          ) : (
            <div>
              <div className="mb-8">
                <div className="velova-mono-label opacity-60 mb-4">Reset password</div>
                <h1 className="velova-display text-[32px] sm:text-[40px] leading-[0.95]">
                  Reset your{' '}
                  <span className="velova-italic" style={{ color: 'var(--velova-blue-deep)' }}>
                    password
                  </span>
                  .
                </h1>
                <p className="text-[14px] opacity-70 mt-4 leading-relaxed">
                  Enter the email associated with your Velova account. If the
                  account exists, we will send a reset link.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="email" className="velova-mono-label opacity-70">
                      Work email
                    </label>
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                    disabled={loading}
                    autoComplete="email"
                    className="w-full bg-transparent border-0 border-b border-[var(--velova-line-strong)] pb-3 pt-1 text-[18px] focus:outline-none focus:border-[var(--velova-blue-deep)] transition-colors placeholder:opacity-40"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="velova-btn velova-btn-ink w-full justify-center disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-[14px] h-[14px] animate-spin" />
                      Sending reset link
                    </>
                  ) : (
                    <>
                      Send reset link
                      <ArrowUpRight className="w-[14px] h-[14px]" />
                    </>
                  )}
                </button>

                <div className="velova-hr" />

                <Link
                  href="/login"
                  className="flex items-center gap-1.5 text-[13px] hover:opacity-70 transition-opacity"
                >
                  Return to sign in
                  <ArrowUpRight className="w-[12px] h-[12px]" />
                </Link>
              </form>
            </div>
          )}
        </div>
      </div>

      <div className="p-5 sm:p-8 flex items-center justify-between text-[11px] opacity-50">
        <span>{SITE_META.copyright}</span>
        <span className="velova-mono-label">v1.0</span>
      </div>
    </main>
  )
}
